import {keysFirstLetterToLowerCase} from './callSessionUtils';

type MonitoringFilter = {key: string; value: any};

function getFiltersByKey(filters: MonitoringFilter[] | undefined, keys: string | string[]): MonitoringFilter[] {
    const keyList = Array.isArray(keys) ? keys : [keys];
    return (filters || []).filter(filter => keyList.includes(filter.key));
}

function getFilterByKey(filters: MonitoringFilter[] | undefined, key: string): MonitoringFilter | undefined {
    return (filters || []).find(filter => filter.key === key);
}

function isMissing(value: any): boolean {
    return value === null || value === undefined || value === '';
}

function valuesMatch(itemValue: any, filterValue: any, mode: 'includes' | 'exact'): boolean {
    if (Array.isArray(itemValue)) {
        return itemValue.some(value => valuesMatch(value, filterValue, mode));
    }

    if (isMissing(itemValue) && itemValue !== 0 && itemValue !== false) {
        return false;
    }

    if (mode === 'includes') {
        return String(itemValue).includes(String(filterValue));
    }

    return String(itemValue) === String(filterValue);
}

function matchesGroup(filters: MonitoringFilter[], itemValue: any, mode: 'includes' | 'exact' = 'exact'): boolean {
    if (!filters.length) {
        return true;
    }

    return filters.some(filter => valuesMatch(itemValue, filter.value, mode));
}

function matchesDateRange(itemDate: any, filterValue: string): boolean {
    if (!filterValue) {
        return true;
    }

    const [startFilterDateStr, endFilterDateStr] = String(filterValue).split(',');
    const startFilterDate = new Date(startFilterDateStr);
    const endFilterDate = new Date(endFilterDateStr);
    const date = new Date(itemDate);

    if (Number.isNaN(date.getTime()) || Number.isNaN(startFilterDate.getTime()) || Number.isNaN(endFilterDate.getTime())) {
        return false;
    }

    return date >= startFilterDate && date <= endFilterDate;
}

function matchesDurationRange(itemValue: any, filterValue: string): boolean {
    const [minDuration, maxDuration] = String(filterValue).split(',').map(num => Number(num.trim()));

    if (Number.isNaN(minDuration) || Number.isNaN(maxDuration)) {
        return true;
    }

    const durationValue = Number(itemValue);

    if (Number.isNaN(durationValue)) {
        return false;
    }

    return durationValue >= minDuration && durationValue <= maxDuration;
}

export function passesAllFilters(newObj: {item?: any}, filters: MonitoringFilter[] | undefined): boolean {
    const item = newObj?.item;

    if (!item) {
        return false;
    }

    const nameToFilter = getFilterByKey(filters, 'ANI');
    const aNumberFilter = getFilterByKey(filters, 'ANumber');
    const ownerCallFilters = getFiltersByKey(filters, ['OwnerUserName', 'OwnerCall']);
    const usernameFilters = getFiltersByKey(filters, 'UserName');
    const directionFilters = getFiltersByKey(filters, 'Direction');
    const ownerTypeFilters = getFiltersByKey(filters, 'OwnerType');
    const campaignIdFilters = getFiltersByKey(filters, 'CampaignId');
    const branchIdFilters = getFiltersByKey(filters, 'BranchId');
    const queueIdFilters = getFiltersByKey(filters, 'QueueId');
    const typeChannelFilters = getFiltersByKey(filters, 'CallType');
    const callResetFilters = getFiltersByKey(filters, 'CallReset');
    const forcedClosingFilters = getFiltersByKey(filters, 'ForcedClosing');
    const declinedFilter = getFilterByKey(filters, 'IsDeclined');
    const startFilter = getFilterByKey(filters, 'Start');
    const endFilter = getFilterByKey(filters, 'End');
    const durationFilter = getFilterByKey(filters, 'Duration');
    const fullCallDurationFilter = getFilterByKey(filters, 'FullCallDuration');
    const reactionClientDurationFilter = getFilterByKey(filters, 'ReactionClient');
    const reactionOperatorDurationFilter = getFilterByKey(filters, 'ReactionOperator');
    const statusFilter = getFiltersByKey(filters, 'Status');

    if (nameToFilter && !valuesMatch(item.aNI || item.ani, nameToFilter.value, 'includes')) {
        return false;
    }

    if (aNumberFilter && !valuesMatch(item.aNumber, aNumberFilter.value, 'includes')) {
        return false;
    }

    if (!matchesGroup(ownerCallFilters, item.ownerUserName || item.ownerCall, 'includes')) {
        return false;
    }

    if (!matchesGroup(usernameFilters, item.userName, 'includes')) {
        return false;
    }

    if (!matchesGroup(directionFilters, item.direction, 'exact')) {
        return false;
    }

    if (!matchesGroup(ownerTypeFilters, item.ownerType, 'exact')) {
        return false;
    }

    if (!matchesGroup(campaignIdFilters, item.campaignId, 'exact')) {
        return false;
    }

    if (!matchesGroup(branchIdFilters, item.branchId, 'exact')) {
        return false;
    }

    if (!matchesGroup(queueIdFilters, item.queueId, 'exact')) {
        return false;
    }

    if (!matchesGroup(typeChannelFilters, item.callType, 'includes')) {
        return false;
    }

    if (!matchesGroup(callResetFilters, item.callReset, 'exact')) {
        return false;
    }

    if (!matchesGroup(forcedClosingFilters, item.forcedClosing, 'exact')) {
        return false;
    }

    if (declinedFilter) {
        const expectedDeclined = String(declinedFilter.value) === '1' || declinedFilter.value === true || declinedFilter.value === 1;
        const actualDeclined = item.isDeclined === true || item.isDeclined === 1 || String(item.isDeclined) === '1';
        if (expectedDeclined !== actualDeclined) {
            return false;
        }
    }

    if (startFilter && !matchesDateRange(item.start, startFilter.value)) {
        return false;
    }

    if (endFilter && !matchesDateRange(item.end, endFilter.value)) {
        return false;
    }

    if (durationFilter && !matchesDurationRange(item.duration, durationFilter.value)) {
        return false;
    }

    if (fullCallDurationFilter && !matchesDurationRange(item.fullCallDuration, fullCallDurationFilter.value)) {
        return false;
    }

    if (reactionClientDurationFilter && !matchesDurationRange(item.reactionClient, reactionClientDurationFilter.value)) {
        return false;
    }

    if (reactionOperatorDurationFilter && !matchesDurationRange(item.reactionOperator, reactionOperatorDurationFilter.value)) {
        return false;
    }

    if (statusFilter.length > 0 && !matchesGroup(statusFilter, item.status, 'exact')) {
        return false;
    }

    return true;
}

function updateOrAddChild(children: any[], newChild: any): any[] {
    const list = Array.isArray(children) ? children : [];
    const existingIndex = list.findIndex(child => child.item?.id === newChild.item?.id);

    if (existingIndex > -1) {
        return list.map((child, index) =>
            index === existingIndex
                ? {
                    ...child,
                    ...newChild,
                    item: {
                        ...child.item,
                        ...newChild.item
                    },
                    children: updateDataByGroupSessionIdWithNew(
                        child.children || [],
                        newChild.children || [],
                        []
                    )
                }
                : child
        );
    }

    return [...list, newChild];
}

function cleanChildren(children: any[], incomingChildren: any[]): any[] {
    if (!children?.length) {
        return [];
    }

    if (!incomingChildren?.length) {
        return children;
    }

    return children
        .map(child => {
            const existsInIncoming = incomingChildren.some(newObj =>
                newObj.item?.id === child.item?.id ||
                newObj.item?.bSessionId === child.item?.bSessionId
            );

            if (!existsInIncoming) {
                return null;
            }

            return {
                ...child,
                children: cleanChildren(child.children, incomingChildren)
            };
        })
        .filter(Boolean);
}

export function updateDataByGroupSessionIdWithNew(data: any[], newData: any[], filters: MonitoringFilter[] | undefined): any[] {
    const source = Array.isArray(data) ? data : [];
    const incoming = Array.isArray(newData) ? newData : [];
    const existingIds = new Set(source.map(obj => obj?.item?.groupSessionId).filter(Boolean));

    const updatedData = source
        .map(obj => {
            const newObj = incoming.find(incomingObj => incomingObj.item?.groupSessionId === obj.item?.groupSessionId);

            if (!newObj) {
                return obj;
            }

            if (!passesAllFilters(newObj, filters)) {
                return null;
            }

            const incomingChildren = Array.isArray(newObj.children) ? newObj.children : [];

            return {
                ...obj,
                item: {
                    ...obj.item,
                    ...newObj.item
                },
                children: incomingChildren.length
                    ? incomingChildren.reduce(
                        (updatedChildren: any[], newChild: any) => updateOrAddChild(updatedChildren, newChild),
                        cleanChildren(obj.children, incomingChildren)
                    )
                    : obj.children
            };
        })
        .filter(Boolean);

    let newEntries: any[] = [];
    const statusFilter = getFiltersByKey(filters, 'Status');
    const hasOnlyStatus101Filter = statusFilter.length === 1 && String(statusFilter[0]?.value) === '101';

    incoming.forEach(newObj => {
        if (existingIds.has(newObj.item?.groupSessionId)) {
            return;
        }

        if (!passesAllFilters(newObj, filters)) {
            return;
        }

        if (hasOnlyStatus101Filter) {
            const existingObject = newEntries.find(existingObj => existingObj.item?.groupSessionId === newObj.item?.groupSessionId);
            if (existingObject && String(existingObject.item?.status) === '101' && String(newObj.item?.status) !== '101') {
                newEntries = newEntries.filter(existingObj => existingObj.item?.groupSessionId !== newObj.item?.groupSessionId);
                return;
            }
        }

        newEntries.unshift(newObj);
        if (newObj.item?.groupSessionId) {
            existingIds.add(newObj.item.groupSessionId);
        }
    });

    return [...newEntries, ...updatedData];
}

export function sortAndLimitMonitoringList(list: any[], pageSize: number): any[] {
    const next = [...(list || [])];

    next.sort((a, b) => {
        const aTime = new Date(a?.item?.start).getTime();
        const bTime = new Date(b?.item?.start).getTime();
        const aSafe = Number.isNaN(aTime) ? 0 : aTime;
        const bSafe = Number.isNaN(bTime) ? 0 : bTime;
        return bSafe - aSafe;
    });

    const size = Number(pageSize) > 0 ? Number(pageSize) : next.length;
    return next.slice(0, size);
}

export function normalizeHubMonitoringRows(rows: any[]): any[] {
    return (rows || []).map(row => {
        if (!row?.item) {
            return row;
        }

        return {
            ...row,
            item: {
                ...row.item,
                ani: row.item.ani ?? row.item.aNI
            }
        };
    });
}

export function applyMonitoringHubBatches(list: any[], batches: any[][], filters: MonitoringFilter[] | undefined, pageSize: number): any[] {
    let next = Array.isArray(list) ? list : [];

    for (const batch of batches) {
        const normalized = normalizeHubMonitoringRows(keysFirstLetterToLowerCase(batch));
        next = updateDataByGroupSessionIdWithNew(next, normalized, filters);
        next = sortAndLimitMonitoringList(next, pageSize);
    }

    return next;
}

export function parseMonitoringHubPayload(raw: any): any[] | null {
    let parsed = raw;

    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return null;
        }
    }

    if (!Array.isArray(parsed) || !parsed.length) {
        return null;
    }

    return parsed;
}
